import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EvacuationLayout from "@/components/EvacuationLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Check, X, Share2, FileDown, ChevronDown, ChevronUp, Stop, Square,
} from "lucide-react";
import axios from "axios";

const POLL_MS = 10_000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EvacuationActive — used by BOTH admin (token in localStorage) and warden (token in URL prop).
 * If `wardenToken` prop is provided, all API calls go via a special axios instance using that token.
 */
export default function EvacuationActive({ wardenToken }) {
  const nav = useNavigate();
  const isWarden = !!wardenToken;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [endingOpen, setEndingOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const intervalRef = useRef();
  const [collapsedSites, setCollapsedSites] = useState({});

  const client = wardenToken
    ? axios.create({ baseURL: `${BACKEND_URL}/api`, headers: { Authorization: `Bearer ${wardenToken}` } })
    : api;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/evacuation/active");
      setData(data);
      if (!data.active && !isWarden) {
        // No active evac for an admin — bounce back
        toast("No active evacuation");
        nav("/evacuation");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      if (e.response?.status === 410) {
        // Warden link expired
        setData({ active: false, ended: true });
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line
  }, []);

  // every-second tick for "Last updated Xs ago"
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const toggleAccount = async (att, newStatus) => {
    try {
      const url = `/evacuation/${data.event.id}/attendances/${att.id}/${newStatus === "accounted_for" ? "account-for" : "un-account-for"}`;
      await client.post(url);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const issueShareLink = async () => {
    try {
      const { data } = await api.post(`/evacuation/${activeEventId}/share-link`);
      setShareUrl(data.url);
      try { await navigator.clipboard?.writeText(data.url); toast.success("Share link copied"); }
      catch { toast.success("Share link generated"); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const r = await client.get(`/evacuation/${activeEventId}/printable.pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `evacuation-${activeEventId.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const since = (() => {
    if (!data?.last_refreshed_at) return "—";
    const ms = Date.now() - new Date(data.last_refreshed_at).getTime();
    return `${Math.max(0, Math.round(ms / 1000))}s ago`;
    // eslint-disable-next-line
  })();

  const activeEventId = data?.event?.id;

  if (data && data.ended) {
    return (
      <EvacuationLayout eventLabel="ENDED">
        <div className="text-center py-20" data-testid="evac-ended">
          <h2 className="text-2xl font-bold mb-2">Evacuation ended</h2>
          <p className="text-navy-300">This warden link is no longer valid.</p>
        </div>
      </EvacuationLayout>
    );
  }

  return (
    <EvacuationLayout eventLabel={data?.event ? new Date(data.event.started_at).toLocaleTimeString() : ""}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-white">
          <div className="text-3xl font-bold" data-testid="evac-stat-bar">
            <span className="text-success">{data?.stats?.accounted_for ?? 0}</span>
            <span className="text-white opacity-50">/</span>
            <span>{data?.stats?.total ?? 0}</span>
            <span className="text-sm font-normal opacity-70 ml-3">accounted for</span>
          </div>
          <div className="text-xs text-white opacity-60 mt-1">
            Last updated {since}
            {data?.event?.started_by_label ? ` · started by ${data.event.started_by_label}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm inline-flex items-center gap-1.5"
            data-testid="evac-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {!isWarden && (
            <>
              <button onClick={issueShareLink}
                className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm inline-flex items-center gap-1.5"
                data-testid="evac-share">
                <Share2 className="w-4 h-4" /> Share with wardens
              </button>
              <button onClick={() => setEndingOpen(true)}
                className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                data-testid="evac-end-btn">
                <Square className="w-4 h-4" /> End evacuation
              </button>
            </>
          )}
          <button onClick={downloadPdf}
            className="rounded-lg bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm inline-flex items-center gap-1.5"
            data-testid="evac-pdf">
            <FileDown className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {shareUrl && (
        <div className="rounded-lg bg-white/10 border border-white/20 p-3 mb-4 text-xs text-white" data-testid="evac-share-url">
          <div className="opacity-70 mb-1">Warden link (TTL 6h):</div>
          <code className="break-all">{shareUrl}</code>
        </div>
      )}

      {!data ? (
        <div className="text-white opacity-60 py-20 text-center">Loading…</div>
      ) : (
        <div className="space-y-4">
          {(data.attendances_by_site || []).map((g) => {
            const collapsed = !!collapsedSites[g.site_id];
            const accountedAtSite = (g.attendances || []).filter((a) => a.status === "accounted_for").length;
            return (
              <section key={g.site_id} className="rounded-xl bg-white text-navy-900" data-testid={`evac-site-section-${g.site_id}`}>
                <button onClick={() => setCollapsedSites((c) => ({ ...c, [g.site_id]: !c[g.site_id] }))}
                  className="w-full flex items-center justify-between px-5 py-4 border-b border-navy-100"
                  data-testid={`evac-site-toggle-${g.site_id}`}>
                  <div className="font-semibold text-lg">{g.site_name}</div>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="text-navy-500">
                      <span className="text-success font-bold">{accountedAtSite}</span>/{g.attendances.length}
                    </span>
                    {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </div>
                </button>
                {!collapsed && (
                  <ul className="divide-y divide-navy-100">
                    {g.attendances.length === 0 ? (
                      <li className="px-5 py-6 text-center text-navy-400 text-sm">No-one signed in here at the start.</li>
                    ) : g.attendances.map((a) => {
                      const ok = a.status === "accounted_for";
                      return (
                        <li key={a.id}
                          className={`px-4 py-3 flex items-center gap-3 transition-colors ${ok ? "bg-success/5" : ""}`}
                          data-testid={`evac-att-${a.id}`}>
                          {a.photo_thumb_url ? (
                            <img src={a.photo_thumb_url} alt="" className="w-14 h-14 rounded-md object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-md bg-navy-100 text-navy-500 font-bold flex items-center justify-center flex-shrink-0 text-lg">
                              {(a.visitor_name || "?").slice(0, 1)}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{a.visitor_name}</div>
                            <div className="text-xs text-navy-500 truncate">
                              {a.company || "—"}{a.host_name ? ` · host: ${a.host_name}` : ""}
                            </div>
                            <div className="text-[10px] text-navy-400 mt-0.5">
                              Signed in {a.signed_in_at ? new Date(a.signed_in_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—"}
                              {a.accounted_for_at ? ` · accounted ${new Date(a.accounted_for_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : ""}
                            </div>
                          </div>
                          <button
                            onClick={() => toggleAccount(a, ok ? "pending" : "accounted_for")}
                            className={`flex-shrink-0 inline-flex items-center justify-center min-w-[64px] min-h-[64px] rounded-xl font-bold transition-all ${
                              ok
                                ? "bg-success text-white"
                                : "bg-orange-500 hover:bg-orange-600 text-white shadow-md"
                            }`}
                            data-testid={`evac-tick-${a.id}`}
                            aria-label={ok ? "Accounted — un-tick" : "Tick as accounted-for"}
                          >
                            {ok ? <Check className="w-7 h-7" /> : <Check className="w-7 h-7" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {endingOpen && (
        <EndEvacModal eventId={activeEventId} onClose={() => setEndingOpen(false)} onDone={() => nav("/evacuation/history")} />
      )}
    </EvacuationLayout>
  );
}

function EndEvacModal({ eventId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim() || !summary.trim()) {
      toast.error("Reason and summary are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/evacuation/${eventId}/end`, { ended_reason: reason, ended_summary: summary });
      toast.success("Evacuation ended — post-incident report saved");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy-900/70 flex items-center justify-center p-4" onClick={onClose} data-testid="evac-end-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg text-navy-900" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-2 inline-flex items-center gap-2">
          <Square className="w-5 h-5 text-red-600" /> End evacuation
        </h3>
        <p className="text-sm text-navy-500 mb-3">
          Snapshots accounted-for / missing counts and tick log into the post-incident report.
          Wardens' share-links are revoked.
        </p>
        <label className="eb-label">Reason</label>
        <input className="eb-input mb-3" placeholder="e.g. all clear given by fire team"
          value={reason} onChange={(e) => setReason(e.target.value)} data-testid="end-reason" />
        <label className="eb-label">Summary</label>
        <textarea rows={4} className="eb-input mb-4"
          placeholder="Brief notes for the incident report"
          value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="end-summary" />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg px-4 py-2"
            data-testid="end-confirm">
            {busy ? "Ending…" : "End now"}
          </button>
        </div>
      </div>
    </div>
  );
}
